import { useReactiveVar } from '@apollo/client';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef } from 'react';

import { posthogLoaded } from '@/modules/analytics/posthog/PosthogProvider';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { diagnostics } from '@/utils/diagnostics';
import { posthogTrackingEnabledVar } from '@/utils/posthogUtils';

export function UserWatcher() {
  const posthogClient = usePostHog();
  const trackingEnabled = useReactiveVar(posthogTrackingEnabledVar);
  const currentUser = useCurrentUser();
  const { usageBucket, tier, applicationConfiguration } =
    useTenantDetailsContext();
  const recordingEnabledRef = useRef<boolean | null>(null);

  useEffect(() => {
    // When the PostHog client instance changes, re-apply our desired recording
    // state exactly once.
    recordingEnabledRef.current = null;
  }, [posthogClient]);

  useEffect(() => {
    // using applicationConfiguration as a heuristic to make sure the tenant data is
    // loaded fully
    if (posthogClient && currentUser && applicationConfiguration) {
      diagnostics.identifyUserWithSession(posthogClient, {
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        userId: currentUser.id,
        tenantUsageBucket: usageBucket,
        tier,
        userKind: currentUser.authorization.userKind,
      });
    }
    // Send profile properties again when tracking changes. The event filter
    // can discard the initial identify event while development tracking is off.
  }, [
    applicationConfiguration,
    currentUser,
    posthogClient,
    tier,
    trackingEnabled,
    usageBucket,
  ]);

  useEffect(() => {
    if (!posthogClient || !applicationConfiguration) {
      return;
    }

    const shouldEnableRecording =
      !applicationConfiguration.disableAdvancedAnalytics && trackingEnabled;

    let cancelled = false;

    // Wait for PostHog to fully load (including the session recording module)
    // before touching session recording to avoid the "Called on script loaded
    // before session recording is available" error.
    void posthogLoaded.then(() => {
      if (cancelled) return;

      if (recordingEnabledRef.current === shouldEnableRecording) {
        return;
      }

      if (!shouldEnableRecording) {
        // Note that this should be a no-op since we already disabled it at init, but we'll
        // do it here for paranoia.
        posthogClient.stopSessionRecording();
        recordingEnabledRef.current = false;
        return;
      }

      // Recording is disabled by default at init; explicitly start it for
      // tenants that allow advanced analytics.
      posthogClient.startSessionRecording(true);
      recordingEnabledRef.current = true;
    });

    return () => {
      cancelled = true;
    };
  }, [applicationConfiguration, posthogClient, trackingEnabled]);

  return null;
}
